import { Link } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { DatePicker } from "design-system/molecules/DatePicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { cn } from "design-system/utils"
import { ArrowUpRightIcon } from "lucide-react"
import { useMemo } from "react"

type Option = {
  label: string
  value: string
}

type Props = {
  title: string
  readOnly?: boolean
  value: string | null | undefined
  onChange?: (value: string | null) => void
  onBlur?: () => void
  placeholder: string
  className?: string
  tooltip?: string
} & (
  | {
      options: Option[] | Readonly<Option[]>
      kind: "select"
    }
  | {
      kind: "input"
    }
  | {
      kind: "date"
    }
  | {
      kind: "link"
      href: string | null
    }
)

export function CustomerInfo({
  title,
  readOnly = false,
  value,
  onChange,
  onBlur,
  placeholder,
  kind,
  className,
  tooltip,
  ...props
}: Props) {
  const input = useMemo(() => {
    if (kind === "select") {
      const options = "options" in props ? props.options : []

      return (
        <Select
          value={value ?? "__none__"}
          onValueChange={(value) => {
            // Radix's hidden native select can emit "" when the value has no matching option
            if (value === "") return
            onChange?.(value === "__none__" ? null : value)
          }}
        >
          <SelectTrigger className="min-w-32">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{"-"}</SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    }

    if (kind === "date") {
      return (
        <DatePicker
          date={value ? new Date(value) : undefined}
          onDateChange={(date) => onChange?.(date ? date.toISOString() : null)}
        />
      )
    }

    return (
      <Input
        placeholder={placeholder}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        className="min-w-40"
      />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, placeholder, value, onChange, onBlur])

  const displayValue = useMemo(() => {
    if (kind === "input") {
      const hasValue = value && value.length > 0
      return <span className="text-sm">{hasValue ? value : "-"}</span>
    }

    if (kind === "date") {
      if (!value || value === "") return "-"
      return <span className="text-sm">{new Date(value).toLocaleDateString()}</span>
    }

    if (kind === "link") {
      return (
        <Link
          to={"href" in props && props.href ? props.href : ""}
          className="flex items-center gap-2 text-sm"
        >
          {value}
          <ArrowUpRightIcon className="w-4 h-4" />
        </Link>
      )
    }

    if ("options" in props && value) {
      const label = props.options?.find((option) => option.value === value)?.label

      if (!label) {
        return <span className="text-sm font-mono">{value}</span>
      }

      return <span className="text-sm">{label}</span>
    }

    return <span className="text-muted-foreground italic">{"-"}</span>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, kind])

  return (
    <div
      className={cn("grid grid-cols-[9rem_minmax(0,1fr)] items-center gap-3 min-h-7", className)}
    >
      <div
        className={cn(
          "flex text-muted-foreground text-sm",
          tooltip && "underline decoration-dashed decoration-[0.5px] underline-offset-2",
        )}
      >
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger type="button" className="text-left">
              {title}
            </TooltipTrigger>
            <TooltipContent>{tooltip}</TooltipContent>
          </Tooltip>
        ) : (
          <span>{title}</span>
        )}
      </div>
      <div className="flex min-w-0 flex-col items-start">{readOnly ? displayValue : input}</div>
    </div>
  )
}
